import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../App';
import {
  LayoutDashboard,
  Users,
  Wrench,
  Briefcase,
  Flag,
  FileText,
  Download,
  DollarSign,
  Shield,
  Award,
  Building2,
  ChevronDown,
  ChevronRight,
  Menu,
  X,
  Search,
} from 'lucide-react';
import ThemeToggle from './ThemeToggle';
import DemoModeBanner from './DemoModeBanner';

const IconBox = ({ Icon, size = 20, className = '' }) => {
  return (
    <Icon
      style={{ width: size, height: size }}
      className={`shrink-0 ${className}`}
      strokeWidth={1.8}
    />
  );
};

const LS = {
  sidebarOpen: 'admin.sidebarOpen',
  tradiesExpanded: 'admin.tradiesExpanded',
  jobsExpanded: 'admin.jobsExpanded',
};

const readBool = (key, fallback) => {
  try {
    const v = localStorage.getItem(key);
    if (v === null) return fallback;
    return JSON.parse(v);
  } catch {
    return fallback;
  }
};

const AdminLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, theme } = useAuth();

  // Persisted states
  const [sidebarOpen, setSidebarOpen] = useState(() => readBool(LS.sidebarOpen, true));
  const [tradiesExpanded, setTradiesExpanded] = useState(() => readBool(LS.tradiesExpanded, true));
  const [jobsExpanded, setJobsExpanded] = useState(() => readBool(LS.jobsExpanded, false));

  // Mobile drawer
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  // Optional search
  const [q, setQ] = useState('');

  // For collapsed flyout menu (desktop)
  const [flyout, setFlyout] = useState(null); // { label, items, top, left }
  const flyoutRef = useRef(null);
  const searchRef = useRef(null);
  const searchFocusedRef = useRef(false);

  const isSuperAdmin = user?.admin_sub_role === 'superadmin';

  // Persist localStorage
  useEffect(() => localStorage.setItem(LS.sidebarOpen, JSON.stringify(sidebarOpen)), [sidebarOpen]);
  useEffect(() => localStorage.setItem(LS.tradiesExpanded, JSON.stringify(tradiesExpanded)), [tradiesExpanded]);
  useEffect(() => localStorage.setItem(LS.jobsExpanded, JSON.stringify(jobsExpanded)), [jobsExpanded]);
  useLayoutEffect(() => {
    if (searchFocusedRef.current && searchRef.current) {
      searchRef.current.focus();
    }
  }, [q]);

  // Close mobile drawer on route change
  useEffect(() => {
    setMobileDrawerOpen(false);
    setFlyout(null);
  }, [location.pathname, location.search]);

  // Determine effective theme (supports "system")
  const [effectiveTheme, setEffectiveTheme] = useState('light');

  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)');

    const compute = () => {
      if (theme === 'system') return mq?.matches ? 'dark' : 'light';
      return theme || 'light';
    };

    setEffectiveTheme(compute());

    if (theme !== 'system' || !mq) return;

    const handler = () => setEffectiveTheme(compute());

    // Safari fallback
    if (mq.addEventListener) mq.addEventListener('change', handler);
    else mq.addListener(handler);

    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', handler);
      else mq.removeListener(handler);
    };
  }, [theme]);

  // Public assets helper (supports deployments on subpath)
  const ASSET_BASE = process.env.PUBLIC_URL || '';
  const asset = (p) => `${ASSET_BASE}${p}`;

  // Logos
  const expandedLogoSrc =
    effectiveTheme === 'dark'
      ? asset('/assets/slogowhite.png')
      : asset('/assets/slogocolor.png');

  const collapsedLogoSrc =
    effectiveTheme === 'dark'
      ? asset('/assets/faviconwh.png')
      : asset('/assets/favicon.png');

  // Close flyout on outside click / ESC
  useEffect(() => {
    const onDown = (e) => {
      if (!flyout) return;
      if (flyoutRef.current && flyoutRef.current.contains(e.target)) return;
      setFlyout(null);
    };

    const onKey = (e) => {
      if (e.key === 'Escape') setFlyout(null);
    };

    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [flyout]);

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const menuItems = useMemo(() => {
    const base = [
      { label: 'Dashboard', icon: LayoutDashboard, path: '/admin', exact: true },
      { label: 'Users', icon: Users, path: '/admin/users' },

      {
        label: 'GoTradies',
        icon: Wrench,
        expandable: true,
        expanded: tradiesExpanded,
        onToggle: () => setTradiesExpanded((v) => !v),
        subItems: [
          { label: 'Services', path: '/admin/services' },
          { label: 'Tradie Profiles', path: '/admin/tradies' },
          { label: 'Verification', path: '/admin/verification' },
          { label: 'Quotes', path: '/admin/quotes' },
          { label: 'Reports', path: '/admin/reports' },
        ],
      },

      {
        label: 'GoJobs',
        icon: Briefcase,
        expandable: true,
        expanded: jobsExpanded,
        onToggle: () => setJobsExpanded((v) => !v),
        subItems: [
          { label: 'Job Listings', path: '/admin/gojobs' },
          { label: 'Applications', path: '/admin/applications' },
        ],
      },

      { label: 'GoTalent', icon: Award, path: '/admin/talent' },
      { label: 'Companies', icon: Building2, path: '/admin/companies' },
      { label: 'Feature Flags', icon: Flag, path: '/admin/feature-flags' },
      { label: 'Pricing', icon: DollarSign, path: '/admin/pricing' },
      { label: 'Activity Logs', icon: FileText, path: '/admin/logs' },
      { label: 'Export Data', icon: Download, path: '/admin/export' },
    ];

    if (isSuperAdmin) {
      base.push({ label: 'Super Admin', icon: Shield, path: '/admin/manage-admins' });
    }

    const query = q.trim().toLowerCase();
    if (!query) return base;

    const match = (txt) => String(txt || '').toLowerCase().includes(query);

    return base
      .map((item) => {
        // Non-expandable: filter langsung
        if (!item.expandable) {
          const keep = match(item.label) || match(item.path);
          return keep ? item : null;
        }

        // Expandable: cek parent + subitems
        const parentMatch = match(item.label);

        const filteredSubs = (item.subItems || []).filter(
          (s) => match(s.label) || match(s.path)
        );

        // Kalau parent match, tampilkan semua subitems (biar user tetap bisa lihat struktur)
        if (parentMatch) {
          return {
            ...item,
            expanded: true,
            subItems: item.subItems,
          };
        }

        // Kalau parent nggak match, tapi ada subitems yang match
        if (filteredSubs.length > 0) {
          return {
            ...item,
            expanded: true,
            subItems: filteredSubs,
          };
        }

        // tidak match sama sekali
        return null;
      })
      .filter(Boolean);
  }, [isSuperAdmin, tradiesExpanded, jobsExpanded, q]);

  const openFlyoutFor = (item, anchorEl) => {
    if (!item?.subItems?.length) return;

    // toggle if same
    if (flyout?.label === item.label) {
      setFlyout(null);
      return;
    }

    const rect = anchorEl.getBoundingClientRect();
    const left = Math.min(rect.right + 10, window.innerWidth - 260); // keep inside viewport
    const top = Math.min(rect.top, window.innerHeight - 240);

    setFlyout({
      label: item.label,
      items: item.subItems,
      left,
      top,
    });
  };

  const SidebarContent = ({ isMobile = false }) => (
    <div className="h-full flex flex-col">
      {/* Brand row */}
      <div className="h-16 flex items-center justify-between px-4 border-b border-border">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 rounded-lg hover:bg-muted transition-colors p-1"
          aria-label="GoFella Home"
          title="GoFella Home"
        >
          <img
            src={(isMobile || sidebarOpen) ? expandedLogoSrc : collapsedLogoSrc}
            alt="GoFella"
            className={(isMobile || sidebarOpen) ? 'h-8 w-auto object-contain' : 'h-8 w-8 object-contain'}
          />
        </button>

        {isMobile ? (
          <button
            onClick={() => setMobileDrawerOpen(false)}
            className="p-2 hover:bg-muted rounded-lg text-muted-foreground hover:text-foreground"
            aria-label="Close sidebar"
          >
            <IconBox Icon={X} size={20} />
          </button>
        ) : (
          <button
            onClick={() => setSidebarOpen((v) => !v)}
            className="hidden md:inline-flex p-2 hover:bg-muted rounded-lg text-muted-foreground hover:text-foreground"
            aria-label="Toggle sidebar"
            title="Toggle sidebar"
          >
            <IconBox Icon={sidebarOpen ? X : Menu} size={20} />
          </button>
        )}
      </div>

      {/* Search */}
      {(isMobile || sidebarOpen) && (
        <div className="p-4 border-b border-border">
          <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
              <IconBox Icon={Search} size={16} />
            </div>
            <input
              ref={searchRef}
              value={q}
              onFocus={() => (searchFocusedRef.current = true)}
              onBlur={() => (searchFocusedRef.current = false)}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
              placeholder="Search menu..."
              className="w-full pl-9 pr-3 py-2 rounded-xl border border-border bg-background text-sm outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
        </div>
      )}

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto p-3">
        <div className="space-y-1">
          {menuItems.length === 0 ? (
            <div className="px-3 py-2 text-sm text-muted-foreground">
              No results
            </div>
          ) : (
            menuItems.map((item, idx) => {
              const parentActive =
                item.expandable && item.subItems?.some((s) => isActive(s.path));

              if (item.expandable) {
                // EXPANDABLE
                if (isMobile || sidebarOpen) {
                  // normal expandable with roll up
                  return (
                    <div key={idx}>
                      <button
                        onClick={item.onToggle}
                        className={`w-full flex items-center justify-between px-3 py-2 rounded-xl transition-colors ${
                          parentActive ? 'bg-muted' : 'hover:bg-muted'
                        } text-foreground`}
                      >
                        <div className="flex items-center gap-3">
                          <IconBox Icon={item.icon} size={20} />
                          <span className="font-medium">{item.label}</span>
                        </div>
                        <IconBox Icon={item.expanded ? ChevronDown : ChevronRight} size={16} />
                      </button>

                      {item.expanded && (
                        <div className="ml-4 mt-1 space-y-1">
                          {item.subItems.map((sub, sIdx) => (
                            <button
                              key={sIdx}
                              onClick={() => {
                                if (isMobile) setMobileDrawerOpen(false);
                                navigate(sub.path);
                              }}
                              className={`w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm transition-colors ${
                                isActive(sub.path)
                                  ? 'bg-primary text-primary-foreground'
                                  : 'hover:bg-muted text-muted-foreground hover:text-foreground'
                              }`}
                            >
                              <div className="w-1.5 h-1.5 rounded-full bg-current" />
                              <span>{sub.label}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                }

                // collapsed desktop: icon button opens flyout menu
                return (
                  <div key={idx} className="relative">
                    <button
                      onClick={(e) => openFlyoutFor(item, e.currentTarget)}
                      className={`w-full flex items-center justify-center px-3 py-2 rounded-xl transition-colors ${
                        parentActive
                          ? 'bg-primary text-primary-foreground'
                          : 'hover:bg-muted text-foreground'
                      }`}
                      title={item.label}
                      aria-label={item.label}
                    >
                      <IconBox Icon={item.icon} size={20} />
                    </button>
                  </div>
                );
              }

              // NON EXPANDABLE
              return (
                <div key={idx}>
                  <button
                    onClick={() => {
                      if (isMobile) setMobileDrawerOpen(false);
                      navigate(item.path);
                    }}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl transition-colors ${
                      isActive(item.path, item.exact)
                        ? 'bg-primary text-primary-foreground'
                        : 'hover:bg-muted text-foreground'
                    }`}
                    title={!sidebarOpen && !isMobile ? item.label : undefined}
                    aria-label={item.label}
                  >
                    <IconBox Icon={item.icon} size={20} />
                    {(isMobile || sidebarOpen) && <span className="font-medium">{item.label}</span>}
                  </button>
                </div>
              );
            })
          )}
        </div>
      </nav>

      {/* User info */}
      {(isMobile || sidebarOpen) && user && (
        <div className="p-4 border-t border-border">
          <div className="text-sm text-muted-foreground mb-1">Logged in as</div>
          <div className="font-semibold text-foreground truncate">{user.full_name}</div>
          <div className="text-xs text-muted-foreground truncate">{user.email}</div>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-background flex">
      {/* Desktop sidebar */}
      <aside
        className={`hidden md:block fixed left-0 top-0 h-full bg-card border-r border-border transition-all duration-300 z-40 ${
          sidebarOpen ? 'w-60' : 'w-16'
        }`}
      >
        <SidebarContent />
      </aside>

      {/* Collapsed flyout menu (desktop only) */}
      {!sidebarOpen && flyout && (
        <div
          ref={flyoutRef}
          className="hidden md:block fixed z-[60] w-56 rounded-xl border border-border bg-background shadow-lg overflow-hidden"
          style={{ top: flyout.top, left: flyout.left }}
        >
          <div className="px-3 py-2 text-xs font-medium text-muted-foreground border-b border-border">
            {flyout.label}
          </div>
          <div className="p-1">
            {flyout.items.map((sub, i) => (
              <button
                key={i}
                onClick={() => {
                  setFlyout(null);
                  navigate(sub.path);
                }}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                  isActive(sub.path)
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-muted text-foreground'
                }`}
              >
                {sub.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Mobile drawer + overlay */}
      {mobileDrawerOpen && (
        <div className="md:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileDrawerOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-72 bg-card border-r border-border">
            <SidebarContent isMobile />
          </aside>
        </div>
      )}

      {/* Main content */}
      <div className={`flex-1 transition-all duration-300 ${sidebarOpen ? 'md:ml-60' : 'md:ml-16'}`}>
        {/* Top header */}
        <header className="sticky top-0 z-30 h-16 bg-background/95 backdrop-blur border-b border-border">
          <div className="h-full px-4 md:px-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setMobileDrawerOpen(true)}
                className="md:hidden p-2 hover:bg-muted rounded-lg"
                aria-label="Open sidebar"
              >
                <IconBox Icon={Menu} size={20} />
              </button>

              <div className="flex items-center gap-2">
                <h1 className="text-lg md:text-xl font-bold text-foreground">Admin Console</h1>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <ThemeToggle />
              <button
                onClick={() => navigate('/profile')}
                className="hidden sm:block text-sm text-muted-foreground hover:text-foreground"
              >
                Profile
              </button>
              <button
                onClick={logout}
                className="hidden sm:block px-4 py-2 border border-border rounded-xl hover:bg-muted text-foreground"
              >
                Logout
              </button>
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="p-4 md:p-6">
          <DemoModeBanner />
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
